import { useState, useEffect } from "react";
import { PlayCircle, Loader2, X, Shield, AlertTriangle, Flame } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { handleReviveAd, hasUsedMonetagRevive, MONETAG_REVIVE_URL } from "@/lib/adManager";
import { isRewardedReady } from "@/lib/h5ads";
import ReviveGrantedBanner from "@/components/game/ReviveGrantedBanner";

/**
 * BossDefeatReviveModal — high-conversion revive prompt shown on BOSS-FLOOR deaths.
 * Uses the migrated Google H5 adBreak() flow (NOT popups) so the sponsor-queue
 * backend is the only common failure mode.
 *
 *  - Primary CTA: "Watch Ad to Revive"  → H5 rewarded ad, grants 50% HP
 *  - Secondary:   "Accept Defeat"        → closes modal, run stays dead
 *  - Fallback:    no fill / ad-blocker → inline warning + Retry + Ad-Free Pass offer
 */
export default function BossDefeatReviveModal({ open, run, onRevived, onDismiss, onOpenStore }) {
  const [busy, setBusy] = useState(false);
  const [adUnavailable, setAdUnavailable] = useState(false);
  const [used, setUsed] = useState(() => hasUsedMonetagRevive());
  const [reviveGranted, setReviveGranted] = useState(null);

  // Pre-flight: if the rewarded SDK isn't ready (current Monetag zone type
  // doesn't expose a show_<zone>() fn), skip the "WATCH AD" dance and show
  // the one-click FREE REVIVE as the primary CTA from the start.
  useEffect(() => {
    if (!open) return;
    // Give the SDK up to 2s to register before deciding.
    const check = () => {
      if (!isRewardedReady()) setAdUnavailable(true);
    };
    const t = setTimeout(check, 2000);
    return () => clearTimeout(t);
  }, [open]);

  if (!open) return null;

  const disabled = used || !MONETAG_REVIVE_URL;

  const watchAd = async () => {
    if (busy || disabled) return;
    setAdUnavailable(false);
    setBusy(true);
    const result = await handleReviveAd();
    if (result.ok) {
      setUsed(true);
      setReviveGranted({
        floor:      result.data.floor,
        health:     result.data.health,
        max_health: result.data.max_health,
        payload:    result.data,
      });
      toast.success(`Boss floor revive! You return with ${result.data.health} HP.`);
    } else if (result.reason === "ad_unavailable" || result.reason === "popup_blocked") {
      setAdUnavailable(true);
      toast.error("Ad unavailable — the sponsor queue is empty or an ad-blocker is active.");
    } else if (result.reason === "dismissed") {
      toast.info("Ad closed early — reward not granted.");
    } else if (result.reason === "already_used") {
      setUsed(true);
      toast.info("You've already used your revive this run.");
    } else {
      toast.error(result.detail || "Revive unavailable.");
    }
    setBusy(false);
  };

  // iter67.9 — Fallback revive when ads are blocked / unavailable.
  // Rate-limited server-side to 2/day/user.
  const fallbackRevive = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const res = await api.post("/ads/reward/continue-fallback", {});
      setUsed(true);
      setReviveGranted({
        floor:      res.floor,
        health:     res.health,
        max_health: res.max_health,
        payload:    res,
      });
      toast.success(
        `The Ember lights your torch anyway. ${res.used_today}/${res.daily_limit} free revives used today.`,
        { duration: 5000 }
      );
    } catch (e) {
      const detail = e?.response?.data?.detail || "Fallback revive failed.";
      toast.error(detail);
    }
    setBusy(false);
  };

  return (
    <div
      className="fixed inset-0 z-[65] flex items-center justify-center px-4"
      data-testid="boss-defeat-modal"
      style={{ background: "rgba(0,0,0,0.90)", backdropFilter: "blur(10px)" }}
    >
      <div
        className="panel p-7 max-w-md w-full relative overflow-hidden"
        style={{
          background: "radial-gradient(ellipse at top, rgba(90,20,10,0.65) 0%, rgba(10,3,2,0.98) 60%)",
          borderColor: "rgba(226,88,34,0.65)",
          boxShadow: "0 0 60px rgba(226,88,34,0.35)",
        }}
      >
        <button
          onClick={onDismiss}
          className="absolute top-3 right-3 text-[#8b7965] hover:text-[#f0e6d2] transition-colors"
          data-testid="boss-defeat-close"
          aria-label="Accept defeat"
        >
          <X size={18} />
        </button>

        <div className="text-center">
          <div className="inline-flex items-center gap-2 text-[10px] tracking-[0.5em] ember">
            <Shield size={12} /> RUN INTERRUPTED <Shield size={12} />
          </div>
          <h2 className="font-heading text-3xl md:text-4xl text-[#f0e6d2] mt-2">
            {adUnavailable
              ? <>Second <span className="ember italic">Chance!</span></>
              : <>Boss Floor <span className="ember italic">Defeat!</span></>}
          </h2>
          <p className="font-narrative text-sm md:text-base ash-text italic mt-4 leading-relaxed">
            {adUnavailable
              ? <>Don't lose your run. Claim a <span className="gold-text">free revive</span> on the house — 2 per day — and finish the fight.</>
              : <>Don't lose your run progress. Watch a short video to revive with <span className="gold-text">50% HP</span> and keep fighting.</>}
          </p>
          {run?.floor && (
            <div className="mt-3 text-[10px] tracking-[0.3em] ash-text">
              FLOOR {run.floor} · {run.chapter_title || "THE DESCENT"}
            </div>
          )}
        </div>

        {busy ? (
          <div className="mt-6 panel py-3 text-center flex items-center justify-center gap-2"
               data-testid="boss-defeat-granting">
            <Loader2 size={14} className="animate-spin" />
            <span className="text-xs tracking-widest ash-text">STRIKING THE PACT...</span>
          </div>
        ) : (
          <>
            {adUnavailable ? (
              // iter67.10 — When the ad network has nothing, promote the FREE REVIVE
              // to the dominant action so players never confuse "watch ad" (which just
              // failed) with the clear path forward.
              <button
                onClick={fallbackRevive}
                data-testid="boss-defeat-free-revive-primary"
                className="mt-6 w-full py-3 flex items-center justify-center gap-3 rounded-sm font-heading tracking-widest text-base transition-all"
                style={{
                  background: "linear-gradient(135deg, #e25822 0%, #ffb458 50%, #e25822 100%)",
                  border: "2px solid #ffd8b8",
                  color: "#1a0805",
                  boxShadow: "0 0 32px rgba(226,88,34,0.75), inset 0 0 20px rgba(255,183,88,0.4)",
                  textShadow: "0 1px 2px rgba(0,0,0,0.3)",
                  fontWeight: 700,
                }}
              >
                <Flame size={20} />
                FREE REVIVE · ON US
              </button>
            ) : (
              <button
                onClick={watchAd}
                disabled={disabled}
                data-testid="boss-defeat-watch-btn"
                className="btn-primary mt-6 w-full py-3 flex items-center justify-center gap-3 disabled:opacity-40 disabled:cursor-not-allowed"
                style={{
                  borderColor: "#e25822",
                  background: "linear-gradient(180deg, #3a1810, #1a0805)",
                  color: "#ffd8b8",
                }}
              >
                <PlayCircle size={18} />
                WATCH AD TO REVIVE
              </button>
            )}

            <button
              onClick={onDismiss}
              data-testid="boss-defeat-accept-btn"
              className="mt-3 w-full text-[10px] tracking-[0.4em] ash-text hover:text-[#f0e6d2] transition-colors"
            >
              ACCEPT DEFEAT
            </button>

            {adUnavailable && (
              <div
                className="mt-3 flex items-start gap-2 p-3 rounded border"
                data-testid="boss-defeat-ad-unavailable"
                style={{ borderColor: "#8a0303", background: "rgba(138,3,3,0.15)" }}
              >
                <AlertTriangle size={14} className="blood-text mt-[2px] flex-shrink-0" />
                <div className="text-[11px] ash-text leading-relaxed">
                  <strong className="blood-text">Google AdSense has nothing to serve right now.</strong>
                  {" "}Use the <strong style={{color:"#ffb458"}}>FREE REVIVE</strong> above — on us, 2 per day.
                  <div className="mt-2 flex flex-wrap gap-3 justify-center">
                    <button
                      onClick={() => { setAdUnavailable(false); watchAd(); }}
                      className="text-[10px] tracking-[0.3em] gold-text underline hover:text-[#f0e6d2]"
                      data-testid="boss-defeat-retry-btn"
                    >
                      RETRY AD
                    </button>
                    {onOpenStore && (
                      <>
                        <span className="text-[10px] ash-text opacity-60">·</span>
                        <button
                          onClick={() => onOpenStore?.("ad_free_pass")}
                          className="text-[10px] tracking-[0.3em] gold-text underline hover:text-[#f0e6d2]"
                          data-testid="boss-defeat-ad-free-btn"
                        >
                          SKIP ADS FOREVER
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}

            {used && (
              <div className="mt-3 text-center text-[10px] tracking-[0.3em] ash-text opacity-70">
                REVIVE ALREADY USED THIS RUN
              </div>
            )}
          </>
        )}
      </div>

      <ReviveGrantedBanner
        open={!!reviveGranted}
        floor={reviveGranted?.floor}
        health={reviveGranted?.health}
        maxHealth={reviveGranted?.max_health}
        onContinue={() => {
          const payload = reviveGranted?.payload;
          setReviveGranted(null);
          onRevived?.(payload);
        }}
      />
    </div>
  );
}
